// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useId, useState, type ReactNode } from 'react';

import { IconButton } from '../../IconButton';
import styles from './ModelSettings.module.css';

export interface RowExpanderProps {
  /** The row itself: a labelled `Checkbox` (Punktsky, Mesh). */
  row: ReactNode;
  /** The disclosure button's accessible name ("Punktsky-indstillinger"). */
  label: string;
  /** Always shown right under the row, before the expanded content (Mesh › Trådnet). */
  sub?: ReactNode;
  /** The row's settings, shown while expanded. None = nothing to configure: no button. */
  children?: ReactNode;
}

/**
 * A Lag row with settings of its own: the row and, at its end, a settings
 * (sliders) button that unfolds them underneath, indented like the row's sub-items.
 * Collapsed by default. The button is the row's sibling, not inside its
 * `<label>`. Renders a fragment so its parts sit in Lag's `.checks` column.
 */
export function RowExpander({ row, label, sub, children }: RowExpanderProps) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const hasContent = children !== undefined && children !== null && children !== false;
  return (
    <>
      <div className={styles.expandRow}>
        {row}
        {hasContent && (
          <IconButton
            label={label}
            variant="filled"
            size="sm"
            className={styles.expandToggle}
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={() => setOpen((o) => !o)}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M4 6h9M19 6h1M4 12h2M11 12h9M4 18h7M16 18h4" />
              <circle cx="16" cy="6" r="2.2" />
              <circle cx="8" cy="12" r="2.2" />
              <circle cx="13" cy="18" r="2.2" />
            </svg>
          </IconButton>
        )}
      </div>
      {sub}
      {hasContent && (
        <div id={bodyId} className={styles.expandBody} hidden={!open}>
          {children}
        </div>
      )}
    </>
  );
}
